import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '@ibk/shared-translation';
import { Button, Icon } from '@ibk/shared-ui';
import cancelIcon from '../../pages/HomePageEmissor/components/assets/migration-menu-cancel.svg';
import invoiceIcon from '../../pages/HomePageEmissor/components/assets/migration-menu-invoice.svg';
import paymentIcon from '../../pages/HomePageEmissor/components/assets/migration-menu-payment.svg';
import reportIcon from '../../pages/HomePageEmissor/components/assets/migration-menu-report.svg';
import styles from './MigrationPreviewOrderMenu.module.css';
const MENU_WIDTH = 220;
const MENU_HEIGHT = 180;
const MENU_TRIGGER_END_INSET = 8;
const rows = [
    { key: 'benefits.orders.list.menu.make-payment', icon: paymentIcon },
    { key: 'benefits.recharge-details.download-report', icon: reportIcon },
    { key: 'benefits.migration.onboarding.menu.invoice', icon: invoiceIcon },
    { key: 'benefits.orders.list.menu.cancel-order', icon: cancelIcon, destructive: true },
];
export function MigrationPreviewOrderMenu({ testIdIdentifier, forceOpen = false, }) {
    const { t } = useTranslation();
    const triggerRef = useRef(null);
    const [position, setPosition] = useState(null);
    useLayoutEffect(() => {
        if (!forceOpen) {
            setPosition(null);
            return;
        }
        const updatePosition = () => {
            const trigger = triggerRef.current?.getBoundingClientRect();
            if (!trigger)
                return;
            const top = Math.min(trigger.bottom + 8, window.innerHeight - MENU_HEIGHT - 8);
            const left = Math.max(8, Math.min(trigger.right - MENU_TRIGGER_END_INSET - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
            setPosition({ top, left });
        };
        updatePosition();
        const observer = new ResizeObserver(updatePosition);
        if (triggerRef.current)
            observer.observe(triggerRef.current);
        const mutationObserver = new MutationObserver(updatePosition);
        for (let element = triggerRef.current; element; element = element.parentElement) {
            mutationObserver.observe(element, { attributes: true, attributeFilter: ['class', 'style'] });
        }
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            observer.disconnect();
            mutationObserver.disconnect();
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [forceOpen]);
    return (_jsxs(_Fragment, { children: [_jsx("div", { ref: triggerRef, className: styles.trigger, children: _jsx(Button, { variant: 'tertiary', size: 'small', dataTestId: `button${testIdIdentifier}Trigger`, "aria-label": t('benefits.orders.list.columns.actions'), "aria-haspopup": 'menu', "aria-expanded": forceOpen, "aria-disabled": true, children: _jsx(Icon, { name: 'dotsVerticalOrange', "aria-hidden": true }) }) }), forceOpen &&
                position &&
                createPortal(_jsx("div", { className: styles.menu, role: 'menu', "aria-label": t('benefits.orders.list.columns.actions'), "data-testid": `menu${testIdIdentifier}Menu`, style: position, children: rows.map(row => (_jsxs("button", { type: 'button', role: 'menuitem', "aria-disabled": true, className: [styles.row, row.destructive && styles.destructive].filter(Boolean).join(' '), "data-testid": `${testIdIdentifier}Preview${row.key.split('.').pop()}`, children: [_jsx("img", { src: row.icon, alt: '' }), t(row.key)] }, row.key))) }), document.body)] }));
}
